import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  Snackbar,
  Alert,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import LockResetIcon from "@mui/icons-material/LockReset";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import Section from "./Section";
import { useAuth } from "../../../contexts/AuthContext";

type Props = {
  api: string;
  token?: string;
};

type Role = "admin" | "manager" | "operator";

// Managers see and edit everything an admin does, except this table.
const ROLE_OPTIONS: { value: Role; label: string }[] = [
  { value: "operator", label: "Operator" },
  { value: "manager", label: "Manager" },
  { value: "admin", label: "Admin" },
];

type UserRow = {
  id: number;
  name: string;
  email: string;
  role: Role;
};

const UserManagementSection: React.FC<Props> = ({ api, token }) => {
  const { userEmail } = useAuth();
  const [users, setUsers] = useState<UserRow[]>([]);
  const [loading, setLoading] = useState(true);

  // Add user dialog
  const [addOpen, setAddOpen] = useState(false);
  const [addName, setAddName] = useState("");
  const [addEmail, setAddEmail] = useState("");
  const [addPassword, setAddPassword] = useState("");
  const [addRole, setAddRole] = useState<Role>("operator");
  const [addShowPw, setAddShowPw] = useState(false);
  const [addSaving, setAddSaving] = useState(false);

  // Reset password dialog
  const [resetTarget, setResetTarget] = useState<UserRow | null>(null);
  const [resetPassword, setResetPassword] = useState("");
  const [resetShowPw, setResetShowPw] = useState(false);
  const [resetSaving, setResetSaving] = useState(false);

  // Delete dialog
  const [deleteTarget, setDeleteTarget] = useState<UserRow | null>(null);

  const [snack, setSnack] = useState<{
    open: boolean;
    msg: string;
    severity: "success" | "error";
  }>({ open: false, msg: "", severity: "success" });

  const headers = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  };

  const showSnack = (msg: string, severity: "success" | "error") =>
    setSnack({ open: true, msg, severity });

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${api}/settings/users`, { headers });
      if (!res.ok) throw new Error();
      setUsers(await res.json());
    } catch {
      showSnack("Failed to load users", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  // ---- Add user ----
  const openAdd = () => {
    setAddName(""); setAddEmail(""); setAddPassword("");
    setAddRole("operator"); setAddShowPw(false);
    setAddOpen(true);
  };

  const handleAdd = async () => {
    setAddSaving(true);
    try {
      const res = await fetch(`${api}/settings/users`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          name: addName.trim(),
          email: addEmail.trim(),
          password: addPassword,
          role: addRole,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Create failed");
      }
      showSnack("User created", "success");
      setAddOpen(false);
      await load();
    } catch (e: any) {
      showSnack(e.message || "Create failed", "error");
    } finally {
      setAddSaving(false);
    }
  };

  // ---- Role change ----
  const handleRoleChange = async (user: UserRow, role: Role) => {
    try {
      const res = await fetch(`${api}/settings/users/${user.id}`, {
        method: "PATCH",
        headers,
        body: JSON.stringify({ role }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Update failed");
      }
      setUsers((prev) => prev.map((u) => (u.id === user.id ? { ...u, role } : u)));
      showSnack("Role updated", "success");
    } catch (e: any) {
      showSnack(e.message || "Update failed", "error");
    }
  };

  // ---- Reset password ----
  const openReset = (u: UserRow) => {
    setResetTarget(u);
    setResetPassword("");
    setResetShowPw(false);
  };

  const handleReset = async () => {
    if (!resetTarget) return;
    setResetSaving(true);
    try {
      const res = await fetch(`${api}/settings/users/${resetTarget.id}/reset-password`, {
        method: "POST",
        headers,
        body: JSON.stringify({ password: resetPassword }),
      });
      if (!res.ok && res.status !== 204) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Reset failed");
      }
      showSnack(`Password reset for ${resetTarget.name}`, "success");
      setResetTarget(null);
    } catch (e: any) {
      showSnack(e.message || "Reset failed", "error");
    } finally {
      setResetSaving(false);
    }
  };

  // ---- Delete ----
  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      const res = await fetch(`${api}/settings/users/${deleteTarget.id}`, {
        method: "DELETE",
        headers,
      });
      if (!res.ok && res.status !== 204) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Delete failed");
      }
      showSnack("User deleted", "success");
      setDeleteTarget(null);
      await load();
    } catch (e: any) {
      showSnack(e.message || "Delete failed", "error");
    }
  };

  const addValid = addName.trim() && addEmail.trim() && addPassword.length >= 6;

  return (
    <>
      <Section
        title="Users"
        subtitle="Manage user accounts and roles"
        rightAction={
          <Button startIcon={<AddIcon />} variant="contained" size="small" onClick={openAdd}>
            Add User
          </Button>
        }
      >
        {loading ? (
          <Box display="flex" justifyContent="center" py={3}>
            <CircularProgress size={24} />
          </Box>
        ) : users.length === 0 ? (
          <Typography variant="body2" color="text.secondary">No users found.</Typography>
        ) : (
          <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((u) => {
                  const isSelf = u.email === userEmail;
                  return (
                    <TableRow key={u.id}>
                      <TableCell>
                        {u.name}
                        {isSelf && <Chip label="You" size="small" sx={{ ml: 1 }} />}
                      </TableCell>
                      <TableCell sx={{ wordBreak: "break-all" }}>{u.email}</TableCell>
                      <TableCell>
                        <Select
                          size="small"
                          value={u.role}
                          onChange={(e) => handleRoleChange(u, e.target.value as Role)}
                          disabled={isSelf}
                          sx={{ minWidth: 110 }}
                        >
                          {ROLE_OPTIONS.map((r) => (
                            <MenuItem key={r.value} value={r.value}>
                              {r.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Reset password">
                          <IconButton size="small" onClick={() => openReset(u)}>
                            <LockResetIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={isSelf ? "Cannot delete yourself" : "Delete user"}>
                          <span>
                            <IconButton
                              size="small"
                              color="error"
                              disabled={isSelf}
                              onClick={() => setDeleteTarget(u)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Section>

      {/* Add user dialog */}
      <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Add User</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: "12px !important" }}>
          <TextField
            label="Full name"
            fullWidth
            value={addName}
            onChange={(e) => setAddName(e.target.value)}
          />
          <TextField
            label="Email"
            type="email"
            fullWidth
            value={addEmail}
            onChange={(e) => setAddEmail(e.target.value)}
          />
          <TextField
            label="Initial password"
            type={addShowPw ? "text" : "password"}
            fullWidth
            value={addPassword}
            onChange={(e) => setAddPassword(e.target.value)}
            helperText="Minimum 6 characters"
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setAddShowPw((v) => !v)}>
                      {addShowPw ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }}
          />
          <Select
            value={addRole}
            onChange={(e) => setAddRole(e.target.value as Role)}
            size="small"
          >
            {ROLE_OPTIONS.map((r) => (
              <MenuItem key={r.value} value={r.value}>
                {r.label}
              </MenuItem>
            ))}
          </Select>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAdd}
            disabled={addSaving || !addValid}
          >
            {addSaving ? <CircularProgress size={18} /> : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reset password dialog */}
      <Dialog open={!!resetTarget} onClose={() => setResetTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Reset Password — {resetTarget?.name}</DialogTitle>
        <DialogContent sx={{ pt: "12px !important" }}>
          <TextField
            autoFocus
            label="New password"
            type={resetShowPw ? "text" : "password"}
            fullWidth
            value={resetPassword}
            onChange={(e) => setResetPassword(e.target.value)}
            helperText="Minimum 6 characters"
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setResetShowPw((v) => !v)}>
                      {resetShowPw ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetTarget(null)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleReset}
            disabled={resetSaving || resetPassword.length < 6}
          >
            {resetSaving ? <CircularProgress size={18} /> : "Reset Password"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirm dialog */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs">
        <DialogTitle>Delete User</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>{deleteTarget?.name}</strong> ({deleteTarget?.email})?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete}>Delete</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
      >
        <Alert severity={snack.severity} onClose={() => setSnack((s) => ({ ...s, open: false }))}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </>
  );
};

export default UserManagementSection;
